import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Box,
  Avatar,
  Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ImageViewerModal from './ImageViewerModal';

const EditModal = ({ open, onClose, title, fields, values, onChange, onSubmit, docUploading }) => {
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [viewingImage, setViewingImage] = useState({ url: '', title: '' });

  // Utility function to extract filename from docLink URL
  const extractFileNameFromUrl = (url) => {
    if (!url) return 'Document';
    
    try {
      // Extract the filename after the last slash
      const urlPath = url.split('/').pop();
      
      // If the URL contains an underscore, extract everything after the first underscore
      if (urlPath && urlPath.includes('_')) {
        const parts = urlPath.split('_');
        if (parts.length > 1) {
          // Join all parts after the first underscore (in case filename has underscores)
          return parts.slice(1).join('_');
        }
      }
      
      // If no underscore pattern, return the filename as is
      return urlPath || 'Document';
    } catch (error) {
      console.error('Error extracting filename:', error);
      return 'Document';
    }
  };

  const handleViewDocument = (docPath, docName = null) => {
    // Use extracted filename if docName is not provided or is just a generic name
    const displayName = docName && docName !== 'Document' && !docName.match(/^\d+$/) 
      ? docName 
      : extractFileNameFromUrl(docPath);
      
    setViewingImage({
      url: docPath,
      title: displayName
    });
    setImageViewerOpen(true);
  };
  const renderField = (field) => {
    switch (field.type) {
      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={field.label}
              value={values[field.name] instanceof Date ? values[field.name] : null}
              onChange={(date) => onChange(field.name, date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>
        );
      
      case 'select':
        return (
          <FormControl fullWidth>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={values[field.name] || ''}
              onChange={(e) => onChange(field.name, e.target.value)}
              label={field.label}
              multiple={field.multiple}
            >
              {field.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'file':
      case 'uploadButton':
        return (
          <Stack spacing={1}>
            {values[field.name]?.docPath && (
              <Box 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper'
                }}
              >
                <Avatar 
                  src={values[field.name].docPath} 
                  variant="rounded"
                  sx={{ 
                    width: 60, 
                    height: 60,
                    bgcolor: 'primary.light',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleViewDocument(values[field.name].docPath, values[field.name].docName)}
                >
                  {/* Show first letter if no preview available */}
                  {!values[field.name].docPath && values[field.name].docName?.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'hidden'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {values[field.name].docName && !values[field.name].docName.match(/^\d+$/) 
                      ? values[field.name].docName 
                      : extractFileNameFromUrl(values[field.name].docPath)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Click image to view document
                  </Typography>
                  <Typography variant="caption" color="primary.main" display="block">
                    Use button below to change
                  </Typography>
                  {values[field.name].docPath && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => handleViewDocument(values[field.name].docPath, values[field.name].docName)}
                      sx={{ mt: 0.5, p: 0, minWidth: 'auto' }}
                    >
                      View Document
                    </Button>
                  )}
                </Box>
              </Box>
            )}
            {field.type === 'uploadButton' && field.component ? (
              <Box>
                <field.component 
                  {...field.props}
                  buttonText={values[field.name]?.docPath ? "Change Document" : field.props?.buttonText || field.label}
                />
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                fullWidth
                disabled={docUploading}
                startIcon={docUploading && <CircularProgress size={20} />}
                sx={{ 
                  height: '56px',
                  justifyContent: 'flex-start',
                  textAlign: 'left'
                }}
              >
                {docUploading 
                  ? "Uploading..." 
                  : values[field.name]?.docPath 
                    ? "Change Document" 
                    : field.label
                }
                <input
                  type="file"
                  hidden
                  accept={field.accept || '*/*'}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file && field.onFileChange) {
                      field.onFileChange(file);
                    }
                  }}
                />
              </Button>
            )}
          </Stack>
        );

      default:
        return (
          <TextField
            fullWidth
            label={field.label}
            value={values[field.name] || ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            multiline={field.multiline}
            rows={field.multiline ? 4 : 1}
          />
        );
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {fields.map((field) => (
              <div key={field.name}>
                {renderField(field)}
              </div>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={docUploading}>Cancel</Button>
          <Button 
            onClick={onSubmit} 
            variant="contained" 
            color="primary"
            disabled={docUploading}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Modal */}
      <ImageViewerModal
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        imageUrl={viewingImage.url}
        title={viewingImage.title}
      />
    </>
  );
};

export default EditModal;